<template>
  <SamInput
    ref="numberPicker"
    v-model="inputModel"
    class="sam-number-picker"
    type="number"
    step="step"
    :as-field="asField"
    :disabled="disabled"
    :clearable="false"
    :readonly="readonly"
    @keypress="handleKeyEvents"
  >
    <template #append>
      <div
        v-if="!asField || !readonly"
        class="sam-number-picker__controlsBlock"
      >
        <SamButton
          data-testid="step-button-up"
          icon="arrow-chevron-up"
          :icon-config="{ size: 'xs' }"
          title="Add step number"
          sam-type="text"
          class="step-button"
          :disabled="addDisabled"
          @click="changeStep(true)"
        />
        <SamButton
          data-testid="step-button-down"
          icon="arrow-chevron-down"
          :icon-config="{ size: 'xs' }"
          title="Subtract step number"
          sam-type="text"
          class="step-button"
          :disabled="subtractDisabled"
          @click="changeStep(false)"
        />
      </div>
    </template>
  </SamInput>
</template>

<script>
import SamInput from '../SamInput/SamInput.vue';
import SamButton from '../SamButton/SamButton.vue';
import { debounce } from '../../../utils/utils';

export default {
  name: 'SamNumberPicker',
  components: {
    SamInput,
    SamButton,
  },
  model: {
    prop: 'data',
    event: 'change',
  },
  props: {
    /**
     * The NumberPicker data variable
     */
    data: {
      type: Number,
      required: false,
      default: 0,
    },
    /**
     * The NumberPicker step value
     */
    step: {
      type: Number,
      required: false,
      default: 100,
    },
    /**
     * If the NumberPicker is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the input is on readonly state
     * @values true, false
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the input should look as a field
     * @values true, false
     */
    asField: {
      type: Boolean,
      required: false,
      default: false,
    },
    max: {
      type: Number,
      required: false,
      default: Infinity,
    },
    min: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      model: 0,
    };
  },
  computed: {
    inputModel: {
      get() {
        return this.model;
      },
      set(value) {
        this.handleChange(value);
      },
    },
    addDisabled() {
      return this.disabled || this.model === this.max;
    },
    subtractDisabled() {
      return this.disabled || this.model === this.min;
    },
  },
  watch: {
    data: {
      handler(newVal) {
        this.model = parseInt(newVal, 10);
      },
      immediate: true,
    },
  },
  created() {
    this.emitChange = debounce(this.emitChange, 500);
  },
  mounted() {
    this.$refs.numberPicker.$refs.input.addEventListener('keydown', this.handleKeyEvents);
  },
  beforeDestroy() {
    this.$refs.numberPicker.$refs.input.removeEventListener('keydown', this.handleKeyEvents);
  },
  methods: {
    changeStep(stepUp) {
      const parsedValue = parseInt(this.model, 10);

      // eslint-disable-next-line no-restricted-globals
      this.model = isNaN(parsedValue) ? 0 : parsedValue;
      if (stepUp) {
        if (this.model + this.step > this.max) {
          this.model = this.max;
        } else {
          this.model += this.step;
        }
      } else if (this.model - this.step < this.min) {
        this.model = this.min;
      } else {
        this.model -= this.step;
      }
      this.emitChange();
    },
    emitChange() {
      /**
       * Triggers when NumberPicker changes it's value
       * @event change
       * @property {number} model new value set
       */
      this.$emit('change', this.model);
    },
    handleChange(value) {
      const parsedValue = parseInt(value, 10);
      // eslint-disable-next-line no-restricted-globals
      this.model = isNaN(parsedValue) ? 0 : parsedValue;
      this.emitChange();
    },
    handleKeyEvents(e) {
      const allowed = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        'ArrowLeft',
        'ArrowRight',
        'Backspace',
        'delete',
        'Escape',
        'Tab',
      ];
      if (allowed.indexOf(e.key) === -1) {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'ArrowUp') {
          this.changeStep(true);
        } else if (e.key === 'ArrowDown') {
          this.changeStep(false);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-number-picker::v-deep {
  position: relative;

  .sam-number-picker__controlsBlock {
    position: absolute;
    display: flex;
    gap: $size--1;
    flex-direction: column;
    right: $size--4;
    justify-content: center;
    top: 0;
    bottom: 0;

    .step-button.sam-button {
      @extend .p-0;
      outline: none;

      &:not(:disabled) {
        color: inherit;
      }

      &:focus::after {
        outline: none;
      }

      &:hover {
        color: $informative--main;
        background-color: $informative--surface;
      }
    }
  }

  input {
    padding-right: $size--7;

    &:-moz-ui-invalid,
    &:invalid {
      box-shadow: none;
    }

    // Hide input arrows:
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
